import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import projects from '../../data/projectData.js';
import ProjectDetailModal from '../layout/ProjectDetailModal.js';

gsap.registerPlugin(ScrollTrigger);

// ── Tag colour map ────────────────────────────────────────────────────────────
const TAG_INLINE = {
    'React':        { background: '#FFE600', color: '#111', border: '1.5px solid #111' },
    'Next.js':      { background: '#111',    color: '#fff', border: '1.5px solid #111' },
    'Laravel':      { background: '#FF3366', color: '#fff', border: '1.5px solid #111' },
    'Tailwind CSS': { background: '#4444FF', color: '#fff', border: '1.5px solid #111' },
    'Bootstrap 5':  { background: '#AAFF00', color: '#111', border: '1.5px solid #111' },
    'PostgreSQL':   { background: '#336791', color: '#fff', border: '1.5px solid #111' },
    'MySQL':        { background: '#4479A1', color: '#fff', border: '1.5px solid #111' },
    'default':      { background: '#fff',    color: '#111', border: '1.5px solid #111' },
};
const getTagStyle = (tag) => TAG_INLINE[tag] || TAG_INLINE.default;

// ─────────────────────────────────────────────────────────────────────────────

const ProjectsSection = () => {
    const [selectedProject, setSelectedProject] = useState(null);
    const [activeIndex, setActiveIndex]         = useState(0);

    const sectionRef   = useRef(null);
    const trackWrapRef = useRef(null);
    const trackRef     = useRef(null);
    const progressRef  = useRef(null);
    const touchStartX  = useRef(0);
    const isSwiping    = useRef(false);

    const orderedProjects = [...projects].sort((a, b) => a.id - b.id);
    const total = orderedProjects.length;

    // ── Tablet: go to slide ───────────────────────────────────────────────────
    const goToSlide = useCallback((idx) => {
        const clamped = Math.max(0, Math.min(idx, total - 1));
        setActiveIndex(clamped);
        const track = trackRef.current;
        if (!track) return;
        gsap.to(track, {
            x: -clamped * track.offsetWidth,
            duration: 0.4,
            ease: 'power2.out',
        });
    }, [total]);

    // ── Main GSAP effect ──────────────────────────────────────────────────────
    useEffect(() => {
        // Delay to ensure DOM is measured correctly and Lenis is ready
        const timer = setTimeout(() => {
            const lenis    = window.lenis;
            const section  = sectionRef.current;
            const trackWrap = trackWrapRef.current;
            const track    = trackRef.current;
            const progress = progressRef.current;

            if (!section || !trackWrap || !track) return;

            // ── ScrollerProxy: bridge Lenis ↔ ScrollTrigger ──────────────
            if (lenis) {
                ScrollTrigger.scrollerProxy(document.body, {
                    scrollTop(value) {
                        if (arguments.length) {
                            lenis.scrollTo(value, { immediate: true });
                        }
                        return lenis.scroll;
                    },
                    getBoundingClientRect() {
                        return {
                            top: 0, left: 0,
                            width: window.innerWidth,
                            height: window.innerHeight,
                        };
                    },
                    pinType: document.body.style.transform ? 'transform' : 'fixed',
                });
            }

            // ── matchMedia ────────────────────────────────────────────────
            const mm = gsap.matchMedia();

            /* ────────────────────────────────────────────────────
               DESKTOP ≥1025px — pinned horizontal scroll
            ──────────────────────────────────────────────────── */
            mm.add('(min-width: 1025px)', () => {
                const leftLabel = section.querySelector('.proj-left-label');
                const labelWidth = leftLabel ? leftLabel.offsetWidth : 0;
                const totalScroll = track.scrollWidth - window.innerWidth + labelWidth + 100;

                const animation = gsap.to(track, {
                    x: -totalScroll,
                    ease: 'none',
                    scrollTrigger: {
                        trigger:             section,
                        start:               'top top',
                        end:                 () => `+=${totalScroll}`,
                        pin:                 true,
                        anticipatePin:       1,
                        scrub:               1,
                        invalidateOnRefresh: true,
                        onUpdate: (self) => {
                            if (progress) {
                                progress.style.width = (self.progress * 100) + '%';
                            }
                        },
                    },
                });


                ScrollTrigger.refresh();

                return () => {
                    animation.scrollTrigger?.kill();
                    animation.kill();
                };
            });

            /* ────────────────────────────────────────────────────
               TABLET 768–1024px — touch swipe carousel
            ──────────────────────────────────────────────────── */
            mm.add('(min-width: 768px) and (max-width: 1024px)', () => {
                gsap.set(track, { x: 0 });
                setActiveIndex(0);

                const onTouchStart = (e) => {
                    touchStartX.current = e.touches[0].clientX;
                    isSwiping.current = true;
                };
                const onTouchEnd = (e) => {
                    if (!isSwiping.current) return;
                    isSwiping.current = false;
                    const delta = touchStartX.current - e.changedTouches[0].clientX;
                    if (Math.abs(delta) < 50) return;
                    setActiveIndex(prev => {
                        const next = delta > 0
                            ? Math.min(prev + 1, total - 1)
                            : Math.max(prev - 1, 0);
                        gsap.to(track, {
                            x: -next * track.offsetWidth,
                            duration: 0.4,
                            ease: 'power2.out',
                        });
                        return next;
                    });
                };

                track.addEventListener('touchstart', onTouchStart, { passive: true });
                track.addEventListener('touchend',   onTouchEnd);

                return () => {
                    track.removeEventListener('touchstart', onTouchStart);
                    track.removeEventListener('touchend',   onTouchEnd);
                    gsap.set(track, { x: 0 });
                };
            });

            /* ────────────────────────────────────────────────────
               MOBILE <768px — vertical stagger
            ──────────────────────────────────────────────────── */
            mm.add('(max-width: 767px)', () => {
                gsap.fromTo('.proj-card',
                    { y: 48, opacity: 0 },
                    {
                        y: 0, opacity: 1, stagger: 0.1, duration: 0.6,
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: section,
                            start: 'top 80%',
                            toggleActions: 'play none none reverse',
                            fastScrollEnd: true,
                        },
                    }
                );
            });

            // Universal card hover GSAP animations (replaces CSS hover transitions)
            gsap.utils.toArray('.proj-card').forEach(card => {
                const img = card.querySelector('.proj-card-img img');
                card.addEventListener('mouseenter', () => {
                    gsap.to(card, { y: -6, boxShadow: '8px 8px 0px #000', duration: 0.2, ease: 'power2.out' });
                    if (img) gsap.to(img, { scale: 1.05, duration: 0.3, ease: 'power2.out' });
                });
                card.addEventListener('mouseleave', () => {
                    gsap.to(card, { y: 0, boxShadow: '5px 5px 0px #000', duration: 0.4, ease: 'elastic.out(1,0.5)' });
                    if (img) gsap.to(img, { scale: 1, duration: 0.4, ease: 'power3.out' });
                });
            });

            // Final refresh
            ScrollTrigger.refresh();

            // Cleanup
            return () => {
                mm.revert();
                ScrollTrigger.getAll().forEach(t => t.kill());
            };
        }, 150);  // 150ms delay — after Lenis is set up in App.js

        return () => clearTimeout(timer);
    }, [total]);

    return (
        <>
            <section
                id="projects"
                ref={sectionRef}
                className="proj-section font-jakarta"
            >

                {/* ═══════════════════════════════════════
                    MOBILE HEADING (hidden on desktop/tablet)
                ═══════════════════════════════════════ */}
                <div className="proj-mobile-heading">
                    <div className="proj-pill">
                        <span className="proj-pill-dot" />
                        Projects
                    </div>
                    <h2 className="proj-mobile-h2">
                        My <em className="proj-pink">Work.</em>
                    </h2>
                </div>

                {/* ═══════════════════════════════════════
                    LEFT VERTICAL LABEL (desktop only)
                    FIX 1: styled "My Work." heading
                ═══════════════════════════════════════ */}
                <div className="proj-left-label">

                    {/* Vertical "My Work." heading — single parent, inline children */}
                    <p className="proj-vertical-heading">
                        <span className="proj-vert-my">My </span><span className="proj-vert-work">Work.</span>
                    </p>


                </div>

                {/* ═══════════════════════════════════════
                    TRACK WRAPPER
                ═══════════════════════════════════════ */}
                <div ref={trackWrapRef} className="proj-track-wrap">
                    <div ref={trackRef} className="proj-track">

                        {orderedProjects.map((project, index) => (
                            <div
                                key={project.id}
                                className="proj-card"
                                onClick={() => setSelectedProject(project)}
                            >
                                {/* ── LEFT: Image ── */}
                                <div className="proj-card-img">
                                    <img
                                        src={project.imageUrl}
                                        alt={project.title}
                                        draggable={false}
                                    />
                                    <span className="proj-card-num">
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                </div>

                                {/* ── RIGHT: Content ── */}
                                <div className="proj-card-content">
                                    {/* Tags */}
                                    <div className="proj-tags">
                                        {project.tags.slice(0, 3).map(tag => (
                                            <span
                                                key={tag}
                                                className="proj-tag"
                                                style={getTagStyle(tag)}
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Title */}
                                    <h3 className="proj-card-title">{project.title}</h3>

                                    {/* Description */}
                                    <p className="proj-card-desc">{project.description}</p>

                                    {/* Footer */}
                                    <div className="proj-card-footer">
                                        <button
                                            className="proj-view-btn"
                                            onClick={e => {
                                                e.stopPropagation();
                                                setSelectedProject(project);
                                            }}
                                        >
                                            View Details →
                                        </button>
                                        {project.livePreviewUrl && (
                                            <a
                                                href={project.livePreviewUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="proj-live-btn"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                ↗
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>

                {/* ═══════════════════════════════════════
                    TABLET NAV (dots + arrows)
                ═══════════════════════════════════════ */}
                <div className="proj-tablet-nav">
                    <button
                        className="proj-nav-btn"
                        disabled={activeIndex === 0}
                        onClick={() => goToSlide(activeIndex - 1)}
                        aria-label="Previous"
                    >←</button>
                    <div className="proj-dots">
                        {orderedProjects.map((_, i) => (
                            <button
                                key={i}
                                className={`proj-dot${i === activeIndex ? ' active' : ''}`}
                                onClick={() => goToSlide(i)}
                                aria-label={`Project ${i + 1}`}
                            />
                        ))}
                    </div>
                    <button
                        className="proj-nav-btn"
                        disabled={activeIndex === total - 1}
                        onClick={() => goToSlide(activeIndex + 1)}
                        aria-label="Next"
                    >→</button>
                </div>

                {/* ═══════════════════════════════════════
                    PROGRESS BAR (desktop only)
                ═══════════════════════════════════════ */}
                <div className="proj-progress-wrap">
                    <div ref={progressRef} className="proj-progress-bar" />
                </div>

            </section>

            <AnimatePresence>
                {selectedProject && (
                    <ProjectDetailModal
                        project={selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default ProjectsSection;
